import { memo, MouseEvent, useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useTranslation } from "react-i18next";
import {
  AccountPropertyWarningDefinition,
  Raster,
} from "../../generated/graphql";
import { useComposedRefs } from "../../hooks/useComposedRefs";
import { useFocusOnMount } from "../../hooks/useFocusOnMount";
import { useIsScreenSize } from "../../hooks/useWindowSize";
import { useOutsideAlerter } from "../../utils/outsideAlerter";
import { ActionButton } from "../Common/Buttons/ActionButton";
import { Title } from "../Common/Typography";
import { AccountDocumentTypes } from "./__queries__/map";
import { Container, Header } from "./__styles__/LayersPanel";
import LayerControl from "./LayerControl";
import { BaseMap, FIRM, SavedView } from "./layers";

interface LayersPanelProps {
  firms: Array<FIRM>;
  rasters: Array<Raster>;
  close: (event?: MouseEvent | KeyboardEvent) => void;
  baseMaps: Array<BaseMap>;
  accountDocumentTypes: AccountDocumentTypes;
  accountPropertyWarningDefinitions: Array<AccountPropertyWarningDefinition>;
  savedViews?: Array<SavedView>;
  disableRasterToggle?: boolean;
  disableFirmToggle?: boolean;
  testId?: string;
}

const LayersPanel = ({
  firms,
  rasters,
  close,
  baseMaps,
  accountDocumentTypes,
  accountPropertyWarningDefinitions,
  savedViews,
  disableRasterToggle,
  disableFirmToggle,
  testId,
}: LayersPanelProps) => {
  const { t } = useTranslation();
  const refToFocus = useFocusOnMount<HTMLHeadingElement>();

  const isSmallScreen = useIsScreenSize("mobile", "tablet");

  const containerRef = useRef<HTMLDivElement>(null);
  // This code sets it up so that the layers panel disappears when click on the map on mobile
  useOutsideAlerter({
    ref: containerRef,
    onOutsideInteraction: () => {
      if (isSmallScreen) {
        close();
      }
    },
  });

  const hotkeysRef = useHotkeys<HTMLDivElement>("esc", event => {
    close(event);
  });

  const composedRef = useComposedRefs(containerRef, hotkeysRef);

  const titleId = "map-layers-title";

  return (
    <Container
      role="dialog"
      aria-labelledby={titleId}
      ref={composedRef}
      data-testid={testId}
    >
      <Header>
        <Title
          size="small"
          type="semiBold"
          id={titleId}
          tabIndex={-1}
          ref={refToFocus}
        >
          {t("map-layers-title")}
        </Title>
        <ActionButton
          icon="x"
          onClick={close}
          aria-label="Close Map Layers panel"
          data-testid={`${testId}-close-button`}
        />
      </Header>
      <LayerControl
        firms={firms}
        rasters={rasters}
        baseMaps={baseMaps}
        accountDocumentTypes={accountDocumentTypes}
        accountPropertyWarningDefinitions={accountPropertyWarningDefinitions}
        savedViews={savedViews}
        disableRasterToggle={disableRasterToggle}
        disableFirmToggle={disableFirmToggle}
      />
    </Container>
  );
};

export default memo(LayersPanel);
